import { useRef } from 'react'
import { Icon } from '@iconify/react'
import type { GameStateV2 } from '../game'
import { ClubCrest } from './ClubCrest'
import { useModalFocus } from './useModalFocus'

interface CareerReportDialogProps {
  state: GameStateV2
  onRestart: () => void
}

export function CareerReportDialog({ state, onRestart }: CareerReportDialogProps) {
  const dialogRef = useRef<HTMLElement>(null)
  const history = state.careerHistory
  const lastStage = history.at(-1)
  const completedCareer = lastStage?.stage === 5 && lastStage.outcome === 'completed'
  useModalFocus(true, dialogRef)

  return (
    <div className="dialog-backdrop career-report-backdrop">
      <section ref={dialogRef} className="career-report" role="dialog" aria-modal="true" aria-labelledby="career-report-title" aria-describedby="career-report-summary" tabIndex={-1}>
        <header className="career-report__header">
          <div>
            <div className="career-report__meta">
              <p className="eyebrow">Expediente final · {history.length} {history.length === 1 ? 'etapa disputada' : 'etapas disputadas'}</p>
              <div className="career-report__player" aria-label={`Jugador ${state.player.name}, dorsal ${state.player.number}`}>
                <span className="career-report__number" aria-hidden="true"><small>Dorsal</small><strong>{state.player.number}</strong></span>
                <span className="career-report__player-name"><small>Jugador</small><strong title={state.player.name}>{state.player.name}</strong></span>
              </div>
            </div>
            <h2 id="career-report-title">{completedCareer ? 'Una carrera para la historia' : 'Hasta aquí llegó la carrera'}</h2>
            <p id="career-report-summary">{lastStage?.summary ?? 'La carrera ha terminado. Este es el recorrido que queda para la historia.'}</p>
          </div>
          <span className={`career-report__verdict ${completedCareer ? 'is-complete' : ''}`}><Icon icon={completedCareer ? 'mdi:trophy' : 'mdi:whistle-outline'} />{completedCareer ? 'Leyenda' : 'Final'}</span>
        </header>

        <dl className="career-report__totals" aria-label="Totales de la carrera">
          <div><dt>OVR final</dt><dd>{state.ovr}</dd></div>
          <div><dt>Partidos</dt><dd>{state.appearances}</dd></div>
          <div className="career-report__production-group" role="group" aria-label="Producción ofensiva">
            <dt>Producción</dt>
            <dd className="career-report__production">
              <span><small>Goles</small><strong>{state.goals}</strong></span>
              <span><small>Asistencias</small><strong>{state.assists}</strong></span>
            </dd>
          </div>
        </dl>

        <div className="career-report__history">
          <div className="career-report__history-title"><div><p className="eyebrow">Temporada a temporada</p><h3>Tu recorrido</h3></div><span>Equipo · producción · evolución</span></div>
          <ol>
            {history.map((entry) => (
              <li key={entry.stage} className={entry.outcome === 'eliminated' ? 'is-eliminated' : ''} aria-label={`${entry.stageName} con ${entry.team}: ${entry.appearances} partidos, ${entry.minutesPlayed} minutos, ${entry.goals} goles y ${entry.assists} asistencias`}>
                <span className="career-report__stage">{String(entry.stage).padStart(2, '0')}</span>
                <div className="career-report__team"><ClubCrest name={entry.team} size="medium" /><div><small>{entry.stageName}</small><strong title={entry.team}>{entry.team}</strong><span className="career-report__appearances">{entry.appearances} PJ · {entry.minutesPlayed} min</span></div></div>
                <div className="career-report__stat career-report__stat--goals"><span>Goles</span><strong>{entry.goals}</strong></div>
                <div className="career-report__stat career-report__stat--assists"><span>Asist.</span><strong>{entry.assists}</strong></div>
                <div className="career-report__ovr"><span>OVR</span><strong>{entry.ovrStart} <Icon icon="mdi:arrow-right-thin" /> {entry.ovrEnd}</strong></div>
                <span className={`career-report__result ${entry.outcome === 'completed' ? 'is-complete' : ''}`}><Icon icon={entry.outcome === 'completed' ? 'mdi:check' : 'mdi:close'} />{entry.outcome === 'completed' ? 'Superada' : 'Eliminado'}</span>
              </li>
            ))}
          </ol>
        </div>

        <footer className="career-report__footer">
          <p><Icon icon="mdi:content-save-check-outline" /> Los logros desbloqueados se conservan para futuras carreras.</p>
          <button className="button button--primary" type="button" onClick={onRestart}>Empezar una nueva carrera <Icon icon="mdi:arrow-right" /></button>
        </footer>
      </section>
    </div>
  )
}
